import { Injectable } from '@angular/core';

/**
 * Native Angular port of the AngularJS `parse` factory
 * (account-v1/src/core/parse/parse.js — the pure `ParseService`). 1:1 verbatim:
 * `bool` / `boolOrDefault` / `intOrDefault` / `int` (THROWS on NaN) / `arrayOrDefault`.
 * Replaces the `$injector.get('parse')` bridge for the ~16 native
 * consumers (curation-modal / story-modal / advocacy touchpoint+campaign+contest sheets).
 *
 * `providedIn:'root'` → native consumers `inject(ParseService)`. The AngularJS `parse`
 * factory stays registered for account-v1-internal callers (WS5); the service is
 * STATELESS, so a separate native instance is behaviourally identical. Proven
 * byte-equal to the LIVE `$injector.get('parse')` by
 * account-parity/_diag-parse-service-check.mjs.
 *
 * ⚠ NOT the libs/shared `Parse`/`ParseUtil` (which diverges: `|| 0` default +
 * `.toString()` throws on null), and a SUPERSET of `pipes/parse-value.util.ts parseValue`
 * (which omits the throwing `.int` + `.boolOrDefault`). Mirrors v1 EXACTLY:
 * `parseInt(source, 10)` (no `.toString()`), the `!== undefined` default guard, the
 * `arrayOrDefault` item/datum unwrap, and the `=== true || == '1' || === 'true'` bool.
 */
@Injectable({ providedIn: 'root' })
export class ParseService {
  /** v1 `ParseService.prototype.bool`. */
  bool(source: any): boolean {
    return source === true || source == '1' || source === 'true';
  }

  /** v1 `ParseService.prototype.boolOrDefault` — bool unless source is undefined/null. */
  boolOrDefault(source: any, defaultValue?: any): any {
    if (source !== undefined && source != null) {
      return source === true || source == '1' || source === 'true';
    }
    return defaultValue;
  }

  /** v1 `ParseService.prototype.intOrDefault` — integer, else defaultValue (or 0). */
  intOrDefault(source: any, defaultValue?: any): any {
    const parsed = parseInt(source, 10);
    if (!isNaN(parsed)) return parsed;
    return typeof defaultValue !== 'undefined' ? defaultValue : 0;
  }

  /** v1 `ParseService.prototype.int` — integer or THROWS `'<source> is not a number'`. */
  int(source: any): number {
    const parsed = parseInt(source, 10);
    if (isNaN(parsed)) throw source + ' is not a number';
    return parsed;
  }

  /** v1 `ParseService.prototype.arrayOrDefault` — [] / array / .item / .datum / [source]. */
  arrayOrDefault(array: any): any {
    if (!array) return [];
    if (Array.isArray(array)) return array;
    if (Array.isArray(array.item)) return array.item;
    if (Array.isArray(array.datum)) return array.datum;
    return [array];
  }
}
